import { useState } from 'react'
import { 
  Plus, 
  Globe, 
  Settings, 
  TestTube, 
  Activity,
  Trash2,
  Edit,
  Eye,
  EyeOff
} from 'lucide-react'

interface Webhook {
  id: string
  name: string
  url: string
  events: string[]
  isActive: boolean
  lastTriggered?: string
  status: 'success' | 'failed' | 'pending'
}

const Webhooks: React.FC = () => {
  const [showApiKey, setShowApiKey] = useState(false)
  const [showAddWebhook, setShowAddWebhook] = useState(false)
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null)

  const webhooks: Webhook[] = [
    {
      id: '1',
      name: 'Content Upload Notifications',
      url: 'https://api.acme.com/webhooks/upload',
      events: ['content.uploaded', 'content.processed'],
      isActive: true,
      lastTriggered: '2024-01-15T10:30:00Z',
      status: 'success'
    },
    {
      id: '2',
      name: 'Analytics Events',
      url: 'https://api.acme.com/webhooks/analytics',
      events: ['analytics.view', 'analytics.download'],
      isActive: true,
      lastTriggered: '2024-01-15T09:15:00Z',
      status: 'success'
    },
    {
      id: '3',
      name: 'Security Alerts',
      url: 'https://api.acme.com/webhooks/security',
      events: ['security.breach', 'security.unauthorized'],
      isActive: false,
      lastTriggered: '2024-01-10T14:20:00Z',
      status: 'failed'
    }
  ]

  const availableEvents = [
    'content.uploaded',
    'content.processed',
    'content.deleted',
    'analytics.view',
    'analytics.download',
    'analytics.share',
    'security.breach',
    'security.unauthorized',
    'user.login',
    'user.logout'
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-100'
      case 'failed': return 'text-red-600 bg-red-100'
      case 'pending': return 'text-yellow-600 bg-yellow-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Webhooks</h1>
          <p className="text-gray-600 mt-1">Manage webhook endpoints for real-time notifications</p>
        </div>
        <button
          onClick={() => setShowAddWebhook(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Webhook
        </button>
      </div>

      {/* API Key Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Webhook Secret</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Webhook Secret Key
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                defaultValue="whsec_1234567890abcdef"
                readOnly
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                {showApiKey ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>
          <div className="flex space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Regenerate Secret
            </button>
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
              Copy Secret
            </button>
          </div>
        </div>
      </div>

      {/* Webhooks List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Active Webhooks</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {webhooks.map((webhook) => (
            <div key={webhook.id} className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <Globe className="h-5 w-5 text-blue-500" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{webhook.name}</h4>
                      <p className="text-sm text-gray-500">{webhook.url}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(webhook.status)}`}>
                        {webhook.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        {webhook.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {webhook.lastTriggered && (
                      <span className="text-xs text-gray-500">
                        Last triggered: {new Date(webhook.lastTriggered).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-1">
                      {webhook.events.map((event) => (
                        <span
                          key={event}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {event}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setSelectedWebhook(webhook)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title="Test Webhook"
                  >
                    <TestTube className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setSelectedWebhook(webhook)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title="Edit Webhook"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    className="p-2 text-red-400 hover:text-red-600"
                    title="Delete Webhook"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Webhook Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Successful Deliveries</p>
              <p className="text-2xl font-bold text-gray-900">1,234</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <Activity className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Failed Deliveries</p>
              <p className="text-2xl font-bold text-gray-900">23</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Globe className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Endpoints</p>
              <p className="text-2xl font-bold text-gray-900">{webhooks.filter(w => w.isActive).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Webhook Modal */}
      {showAddWebhook && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Webhook</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Webhook Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter webhook name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endpoint URL
                  </label>
                  <input
                    type="url"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://your-domain.com/webhook"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Events
                  </label>
                  <select
                    multiple
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {availableEvents.map((event) => (
                      <option key={event} value={event}>
                        {event}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id="active" defaultChecked />
                  <label htmlFor="active" className="text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddWebhook(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  Create Webhook
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Webhooks 